import React, { useState, useEffect } from 'react';

type CreateEventFormProps = {
  onSubmit: (eventName: string, eventDate: string) => Promise<void>;
  onCancel?: () => void;
  isSubmitting: boolean;
  initialEventName?: string;
  initialEventDate?: string;
};

const CreateEventForm: React.FC<CreateEventFormProps> = ({ 
  onSubmit, 
  onCancel, 
  isSubmitting,
  initialEventName = '',
  initialEventDate = ''
}) => {
  console.log('📋 CreateEventForm received initial values:', { initialEventName, initialEventDate });
  const [eventName, setEventName] = useState(initialEventName);
  const [eventDate, setEventDate] = useState(initialEventDate);
  const [errors, setErrors] = useState<{ eventName?: string }>({});
  
  // Update form values when initial values change
  useEffect(() => {
    if (initialEventName) {
      console.log('📝 Updating event name from prop:', initialEventName);
      setEventName(initialEventName);
    }
  }, [initialEventName]);
  
  useEffect(() => {
    if (initialEventDate) {
      console.log('📝 Updating event date from prop:', initialEventDate);
      setEventDate(initialEventDate);
    }
  }, [initialEventDate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate
    const newErrors: { eventName?: string } = {};
    if (!eventName.trim()) {
      newErrors.eventName = 'Event name is required';
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length === 0) {
      await onSubmit(eventName.trim(), eventDate);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-xl p-8 max-w-md mx-auto border border-rose-100">
      <h2 className="text-2xl font-bold text-center mb-6 bg-gradient-to-r from-rose-400 to-pink-600 bg-clip-text text-transparent">Create New Event</h2>
      <form onSubmit={handleSubmit} role="form">
        <div className="mb-6">
          <label htmlFor="eventName" className="block text-base font-medium text-gray-700 mb-2">
            Event Name
          </label>
          <input
            id="eventName"
            type="text"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            disabled={isSubmitting}
            className={`w-full px-4 py-3 border rounded-lg text-base outline-none transition-all duration-200 ${
              errors.eventName 
                ? 'border-red-300 bg-red-50 focus:border-red-400' 
                : 'border-rose-200 bg-rose-50/30 focus:border-rose-400 focus:ring-2 focus:ring-rose-100'
            }`}
            placeholder="Sarah & Michael's Wedding"
          />
          {errors.eventName && (
            <div className="text-red-500 text-sm mt-1">{errors.eventName}</div>
          )}
        </div>
        
        <div className="mb-4 sm:mb-6">
          <label htmlFor="eventDate" className="block text-base font-medium text-gray-700 mb-2">
            Event Date
          </label>
          <input
            id="eventDate"
            type="date"
            value={eventDate}
            onChange={(e) => setEventDate(e.target.value)}
            disabled={isSubmitting}
            className="w-full px-4 py-3 border border-rose-200 rounded-lg text-base outline-none transition-all duration-200 bg-rose-50/30 text-gray-700 focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
          />
        </div>
        
        <div className="flex gap-4 justify-end mt-8">
          {onCancel && (
            <button 
              type="button" 
              onClick={onCancel} 
              disabled={isSubmitting} 
              className="px-6 py-3 border-2 border-rose-200 rounded-full bg-white text-gray-700 text-base font-medium cursor-pointer transition-all duration-200 hover:bg-rose-50 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          )}
          <button 
            type="submit" 
            disabled={isSubmitting} 
            className="px-6 py-3 border-none rounded-full bg-gradient-to-r from-rose-400 to-pink-500 text-white text-base font-semibold cursor-pointer transition-all duration-200 hover:shadow-lg transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Creating...' : 'Create Event'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateEventForm; 